import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteHeader from "@/components/SiteHeader";

const inputCls =
  "w-full bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-4 py-3.5 text-white placeholder:text-zinc-600 transition-colors duration-300";
const labelCls = "block font-mono2 text-[10px] tracking-[0.3em] text-zinc-400 uppercase mb-2";

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="login-page">
      <SiteHeader />
      <div className="max-w-md mx-auto px-4 sm:px-8 pt-36 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#E10600] uppercase mb-4 flex items-center gap-4">
            <span className="h-px w-10 bg-[#E10600]" /> Fighter Login
          </p>
          <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9] text-white mb-8">
            Back in the <span className="text-[#E10600]">corner</span>
          </h1>
          <p className="text-base text-zinc-400 leading-relaxed mb-8">
            Log in to your Locker Room to track your sanctioning application, see your scheduled bouts and past
            results, check your official W-L-D record, and raise support tickets with the MPB team.
          </p>
          <form onSubmit={submit} className="border border-zinc-800 bg-[#111111] p-6 sm:p-8 space-y-6" data-testid="login-form">
            <div>
              <label className={labelCls} htmlFor="login-email">Email Address</label>
              <input id="login-email" data-testid="login-email-input" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className={labelCls} htmlFor="login-password">Password</label>
              <input id="login-password" data-testid="login-password-input" required type="password" className={inputCls} placeholder="Your password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full font-cond font-semibold tracking-[0.25em] text-lg bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white py-4 transition-colors duration-300"
            >
              {loading ? "CHECKING..." : "ENTER THE LOCKER ROOM"}
            </button>
            <p className="text-center text-xs text-zinc-500">
              New to MPB?{" "}
              <Link to="/register" data-testid="login-register-link" className="text-[#E10600] hover:underline">Register as a fighter</Link>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
